import React, { useMemo } from 'react';
import type { TableColumnDef } from '../../../shared/table/types.ts';
import { AppDataTable } from '../../../shared/table/AppDataTable.tsx';
import { useAuditorProcessChartingStatus } from '../../../hooks/analytics/useAuditorReports.ts';
import { ProgressBar, RefreshMeta, StatCard } from '../controller/shared.tsx';
import type { AuditorProcessChartingByCycleRow } from '../../../request/analytics/auditorReports.ts';

/**
 * `auditor_process_charting` — charted vs. uncharted processes, by SOX
 * cycle. "charted" = process has ≥1 current flowchart version.
 */
export const ProcessChartingStatusTable: React.FC = () => {
  const { data, isLoading, isError, error, dataUpdatedAt, isFetching, refetch } = useAuditorProcessChartingStatus();
  const rows = useMemo(() => data?.by_cycle ?? [], [data]);

  const columns = useMemo<TableColumnDef<AuditorProcessChartingByCycleRow>[]>(
    () => [
      { id: 'cycle_name', columnLabel: 'Cycle', header: () => 'Cycle', cell: ({ row }) => row.original.cycle_name, widthPercent: 30 },
      { id: 'total', columnLabel: 'Processes', header: () => 'Processes', cell: ({ row }) => row.original.total, widthPercent: 16 },
      { id: 'charted', columnLabel: 'Charted', header: () => 'Charted', cell: ({ row }) => row.original.charted, widthPercent: 16 },
      {
        id: 'progress',
        columnLabel: '% Charted',
        header: () => '% Charted',
        cell: ({ row }) => {
          const pct = row.original.total > 0 ? Math.round((100 * row.original.charted) / row.original.total) : 0;
          return (
            <div className="flex items-center gap-2">
              <div className="w-24"><ProgressBar pct={pct} colorClass="bg-blue-500" /></div>
              <span className="text-xs font-medium text-slate-600">{pct}%</span>
            </div>
          );
        },
        widthPercent: 38,
      },
    ],
    []
  );

  return (
    <div className="space-y-4">
      <div className="analytics-kpi-grid gap-3">
        <StatCard title="Total Processes" value={data?.total_processes ?? 0} color="bg-slate-100 text-slate-600" />
        <StatCard title="Charted" value={data?.charted ?? 0} color="bg-emerald-100 text-emerald-600" />
        <StatCard title="Uncharted" value={data?.uncharted ?? 0} color="bg-amber-100 text-amber-600" />
      </div>
      <AppDataTable<AuditorProcessChartingByCycleRow>
        data={rows}
        columns={columns}
        loading={isLoading}
        error={isError ? error?.message ?? 'Failed to load process charting status.' : null}
        emptyMessage="No processes recorded yet."
        searchable
        paginated
        defaultPageSize={10}
        headerStart={
          <div className="flex w-full justify-end">
            <RefreshMeta updatedAt={dataUpdatedAt} onRefresh={refetch} isFetching={isFetching} />
          </div>
        }
      />
    </div>
  );
};

export default ProcessChartingStatusTable;
